// Client API GLPI : encapsule axios + gestion de session.
// L'instance est créée via createGlpiClient(...) et conservée en mémoire
// pour la durée de vie du task pane.

import axios, { AxiosInstance, AxiosError } from "axios";
import {
  CreateTicketFollowupPayload,
  CreateTicketPayload,
  CreateTicketTaskPayload,
  CreatedRecord,
  GlpiCategory,
  GlpiEntity,
  GlpiError,
  GlpiTicketSearchResponse,
  GlpiUser,
} from "../types/glpi";

export interface GlpiClientConfig {
  apiUrl: string;
  appToken: string;
  userToken: string;
}

export class GlpiClient {
  private http: AxiosInstance;
  private sessionToken: string | null = null;
  private readonly appToken: string;
  private readonly userToken: string;
  public readonly apiUrl: string;

  constructor(config: GlpiClientConfig) {
    this.apiUrl = config.apiUrl.replace(/\/$/, "");
    this.appToken = config.appToken;
    this.userToken = config.userToken;

    this.http = axios.create({
      baseURL: this.apiUrl,
      timeout: 20000,
      headers: {
        "Content-Type": "application/json",
        "App-Token": this.appToken,
      },
    });

    // Intercepteur : injecte Session-Token sur toutes les requêtes après init.
    this.http.interceptors.request.use((req) => {
      if (this.sessionToken) {
        req.headers["Session-Token"] = this.sessionToken;
      }
      // FormData : ne pas forcer application/json (sinon GLPI ne parse pas le multipart).
      if (typeof FormData !== "undefined" && req.data instanceof FormData) {
        delete req.headers["Content-Type"];
      }
      return req;
    });

    // Intercepteur : convertit toute erreur HTTP/réseau en GlpiError typée.
    this.http.interceptors.response.use(
      (r) => r,
      (err: AxiosError) => Promise.reject(this.normalizeError(err))
    );
  }

  // -------------------------------------------------------------------------
  // Session
  // -------------------------------------------------------------------------

  /**
   * Ouvre une session GLPI avec le user_token personnel.
   * Garde le session_token en mémoire.
   */
  public async initSession(): Promise<string> {
    // Debug helper : visible dans la console DevTools du task pane (F12).
    // Permet de vérifier sans afficher les tokens en clair que la conf est bien chargée.
    // eslint-disable-next-line no-console
    console.info("[GLPI] initSession", {
      apiUrl: this.apiUrl,
      appTokenPreview: maskToken(this.appToken),
      userTokenPreview: maskToken(this.userToken),
    });
    const res = await this.http.get<{ session_token: string }>("/initSession", {
      headers: {
        Authorization: `user_token ${this.userToken}`,
      },
    });
    this.sessionToken = res.data.session_token;
    return this.sessionToken;
  }

  public async killSession(): Promise<void> {
    if (!this.sessionToken) return;
    try {
      await this.http.get("/killSession");
    } finally {
      this.sessionToken = null;
    }
  }

  /**
   * Aligne la session API sur l'entité du ticket avant upload Document.
   * Sans cela, GLPI peut répondre HTTP 500 « Une erreur est survenue » en multi-entités.
   */
  public async changeActiveEntities(entitiesId: number, isRecursive = true): Promise<void> {
    await this.ensureSession();
    await this.http.post(
      "/changeActiveEntities",
      { entities_id: entitiesId, is_recursive: isRecursive },
      { params: { session_write: true } }
    );
    // eslint-disable-next-line no-console
    console.info("[GLPI] changeActiveEntities", { entitiesId, isRecursive });
  }

  public hasSession(): boolean {
    return this.sessionToken !== null;
  }

  /** Ouvre une session si nécessaire (ex. avant un lot d'uploads). */
  public async ensureSession(): Promise<void> {
    if (!this.sessionToken) {
      await this.initSession();
    }
  }

  // -------------------------------------------------------------------------
  // Lectures - listes
  // -------------------------------------------------------------------------

  public async listEntities(): Promise<GlpiEntity[]> {
    const res = await this.http.get<GlpiEntity[]>("/Entity", {
      params: { range: "0-100", expand_dropdowns: true },
    });
    return Array.isArray(res.data) ? res.data : [];
  }

  /**
   * Renvoie toutes les catégories ITIL accessibles, sans filtre serveur.
   * Le filtrage par entité (incluant l'héritage récursif) est appliqué côté client
   * par `filterCategoriesForEntity` ci-dessous, car GLPI ne propose pas d'endpoint
   * direct pour cette logique.
   */
  public async listAllCategories(): Promise<GlpiCategory[]> {
    // GLPI paginate à 50 par défaut ; on remonte à 1000 ce qui couvre la grande majorité
    // des installations. Pour les très gros référentiels, prévoir une pagination.
    const res = await this.http.get<GlpiCategory[]>("/ITILCategory", {
      params: {
        range: "0-1000",
      },
    });
    return Array.isArray(res.data) ? res.data : [];
  }

  /**
   * @deprecated Préférer listAllCategories + filterCategoriesForEntity.
   * Le filtre serveur `searchText[entities_id]` fait un LIKE textuel et n'est pas fiable.
   */
  public async listCategoriesByEntity(entityId: number): Promise<GlpiCategory[]> {
    const all = await this.listAllCategories();
    return filterCategoriesForEntity(all, entityId);
  }

  /**
   * Liste les utilisateurs GLPI possédant le profil indiqué.
   * @param techProfileId ID du profil Technicien (6 par défaut sur une instance vierge).
   *
   * Implémentation :
   *  - Utilise /search/Profile_User avec un critère `equals` sur le champ profiles_id (4)
   *    pour un filtre exact (le `searchText` simple fait un LIKE qui matche 6 → 16, 26, etc.).
   *  - Force l'affichage des colonnes pour récupérer l'ID utilisateur et les noms.
   *  - Si la recherche ne renvoie pas les noms (selon version GLPI), on fetch /User/{id}
   *    en parallèle pour les utilisateurs concernés.
   */
  public async listTechnicians(techProfileId: number | number[]): Promise<GlpiUser[]> {
    const profileIds = Array.isArray(techProfileId) ? techProfileId : [techProfileId];
    if (profileIds.length === 0) return [];

    // Approche pragmatique et fiable : on récupère tous les Profile_User en brut
    // (l'objet contient les FK numériques directement : profiles_id, users_id, entities_id)
    // puis on filtre côté client par profiles_id. C'est plus lourd qu'une recherche
    // serveur, mais ça contourne deux pièges du /search/Profile_User :
    //  - searchText[profiles_id]=6 fait un LIKE textuel (matche 6, 16, 26, 60...)
    //  - criteria avec field=4 compare au NOM du profil (expanded) pas à l'ID
    const res = await this.http.get<Array<{ users_id: number; profiles_id: number; entities_id?: number }>>(
      "/Profile_User",
      { params: { range: "0-1000" } }
    );
    const all = Array.isArray(res.data) ? res.data : [];
    const matching = all.filter((r) => profileIds.includes(Number(r.profiles_id)));

    // eslint-disable-next-line no-console
    console.info("[GLPI] listTechnicians", {
      profileIds,
      totalProfileUserRows: all.length,
      matchingRows: matching.length,
    });

    const userIds = Array.from(
      new Set(matching.map((r) => Number(r.users_id)).filter((n) => Number.isFinite(n) && n > 0))
    );

    // Récupération des fiches user complètes (parallèle, limité à 100).
    const ids = userIds.slice(0, 100);
    const users = await Promise.all(ids.map((id) => this.getUser(id).catch(() => null)));
    const result = users.filter((u): u is GlpiUser => u !== null);

    // eslint-disable-next-line no-console
    console.info("[GLPI] listTechnicians résultat", {
      uniqueUserIds: userIds.length,
      usersResolved: result.length,
    });
    return result;
  }

  public async getUser(userId: number): Promise<GlpiUser> {
    const res = await this.http.get<GlpiUser>(`/User/${userId}`);
    return res.data;
  }

  // -------------------------------------------------------------------------
  // Recherche utilisateurs (demandeur)
  // -------------------------------------------------------------------------

  public async searchUserByEmail(email: string): Promise<GlpiUser[]> {
    if (!email) return [];

    // Dans GLPI, l'email d'un user est stocké dans la table UserEmail (jointure 1->N).
    // /search/User champ 5 ne pointe pas vers l'email réel sur toutes les versions.
    // Approche fiable : interroger /UserEmail puis fetch /User/{users_id}.
    let users: GlpiUser[] = [];

    try {
      // 1) Recherche dans UserEmail (table dédiée).
      const res = await this.http.get<Array<{ users_id: number; email: string }>>("/UserEmail", {
        params: {
          "searchText[email]": email,
          range: "0-10",
        },
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      const matchedRows = rows.filter(
        (r) => r.email && r.email.toLowerCase() === email.toLowerCase()
      );
      const userIds = Array.from(
        new Set(matchedRows.map((r) => Number(r.users_id)).filter((n) => Number.isFinite(n) && n > 0))
      );

      // eslint-disable-next-line no-console
      console.info("[GLPI] searchUserByEmail (UserEmail)", {
        email,
        userEmailRows: rows.length,
        exactMatches: matchedRows.length,
        userIds,
      });

      if (userIds.length > 0) {
        const fetched = await Promise.all(userIds.map((id) => this.getUser(id).catch(() => null)));
        users = fetched.filter((u): u is GlpiUser => u !== null);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[GLPI] searchUserByEmail via /UserEmail a échoué", (e as Error).message);
    }

    // 2) Fallback : si rien trouvé via UserEmail, essai sur le champ email primaire
    //    de la fiche User (stocké dans certaines versions comme champ direct).
    if (users.length === 0) {
      try {
        const res = await this.http.get<{ data?: Array<Record<string, string | number>> }>(
          "/search/User",
          {
            params: {
              "criteria[0][field]": 5, // email (sur certaines versions GLPI)
              "criteria[0][searchtype]": "equals",
              "criteria[0][value]": email,
              "forcedisplay[0]": 2,
              range: "0-5",
            },
          }
        );
        const rows = res.data?.data ?? [];
        const ids = rows
          .map((row) => Number(row["2"]))
          .filter((n) => Number.isFinite(n) && n > 0);
        if (ids.length > 0) {
          const fetched = await Promise.all(ids.map((id) => this.getUser(id).catch(() => null)));
          users = fetched.filter((u): u is GlpiUser => u !== null);
        }
      } catch {
        // ignore
      }
    }

    return users;
  }

  public async searchUserByName(query: string): Promise<GlpiUser[]> {
    if (!query) return [];
    const res = await this.http.get<{ data?: Array<Record<string, string | number>> }>("/search/User", {
      params: {
        "criteria[0][field]": 1,
        "criteria[0][searchtype]": "contains",
        "criteria[0][value]": query,
        "criteria[0][link]": "AND",
        "criteria[1][field]": 9,
        "criteria[1][searchtype]": "contains",
        "criteria[1][value]": query,
        "criteria[1][link]": "OR",
        "forcedisplay[0]": 2,
        "forcedisplay[1]": 1,
        "forcedisplay[2]": 9,
        "forcedisplay[3]": 34,
        "forcedisplay[4]": 5,
        range: "0-10",
      },
    });
    const rows = res.data?.data ?? [];
    return rows.map((row) => ({
      id: Number(row["2"]),
      name: String(row["1"] ?? ""),
      realname: row["9"] ? String(row["9"]) : undefined,
      firstname: row["34"] ? String(row["34"]) : undefined,
      email: row["5"] ? String(row["5"]) : undefined,
    }));
  }

  // -------------------------------------------------------------------------
  // Recherche tickets existants
  // -------------------------------------------------------------------------

  public async searchTickets(query: string): Promise<GlpiTicketSearchResponse> {
    const idMatch = query.match(/^#?(\d+)$/);
    const params: Record<string, string | number> = {
      "forcedisplay[0]": 2, // id
      "forcedisplay[1]": 1, // name
      "forcedisplay[2]": 12, // status
      "forcedisplay[3]": 80, // entity name
      "sort[0]": 2,
      "order[0]": "DESC",
      range: "0-10",
    };

    if (idMatch) {
      // Recherche exacte par ID.
      params["criteria[0][field]"] = 2;
      params["criteria[0][searchtype]"] = "equals";
      params["criteria[0][value]"] = idMatch[1];
    } else {
      // Recherche par titre (champ 1).
      params["criteria[0][field]"] = 1;
      params["criteria[0][searchtype]"] = "contains";
      params["criteria[0][value]"] = query;
    }

    const res = await this.http.get<GlpiTicketSearchResponse>("/search/Ticket", { params });
    return res.data;
  }

  // -------------------------------------------------------------------------
  // Créations
  // -------------------------------------------------------------------------

  public async createTicket(payload: CreateTicketPayload): Promise<CreatedRecord> {
    const res = await this.http.post<CreatedRecord | CreatedRecord[]>("/Ticket", { input: payload });
    // GLPI peut renvoyer un tableau si batch ; on normalise.
    const data = Array.isArray(res.data) ? res.data[0] : res.data;
    return data;
  }

  public async createTicketTask(ticketId: number, payload: CreateTicketTaskPayload): Promise<CreatedRecord> {
    const res = await this.http.post<CreatedRecord | CreatedRecord[]>(`/Ticket/${ticketId}/TicketTask`, {
      input: payload,
    });
    const data = Array.isArray(res.data) ? res.data[0] : res.data;
    return data;
  }

  public async addTicketFollowup(
    ticketId: number,
    payload: CreateTicketFollowupPayload
  ): Promise<CreatedRecord> {
    const res = await this.http.post<CreatedRecord | CreatedRecord[]>(
      `/Ticket/${ticketId}/TicketFollowup`,
      { input: payload }
    );
    const data = Array.isArray(res.data) ? res.data[0] : res.data;
    return data;
  }

  /**
   * Lit l'entité GLPI d'un ticket existant (requis pour les droits Document en multi-entités).
   */
  public async fetchTicketEntitiesId(ticketId: number): Promise<number | undefined> {
    try {
      const res = await this.http.get<Record<string, unknown>>(`/Ticket/${ticketId}`);
      const raw = res.data;
      let row: Record<string, unknown> | undefined;
      if (raw && typeof raw === "object") {
        if ("entities_id" in raw) {
          row = raw;
        } else {
          const firstKey = Object.keys(raw)[0];
          const nested = firstKey ? (raw[firstKey] as unknown) : undefined;
          if (nested && typeof nested === "object") {
            row = nested as Record<string, unknown>;
          }
        }
      }
      const eid = Number(row?.entities_id);
      if (Number.isFinite(eid) && eid > 0) {
        // eslint-disable-next-line no-console
        console.info("[GLPI] fetchTicketEntitiesId", { ticketId, entitiesId: eid });
        return eid;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[GLPI] fetchTicketEntitiesId échoué", ticketId, (e as Error).message);
    }
    return undefined;
  }

  /**
   * Upload d'un fichier vers GLPI puis liaison au ticket.
   * Cf. apirest.md > "Upload a document file" + forum GLPI Document_Item.
   *
   * Comme à la création de ticket : aligne la session sur entities_id du ticket
   * (changeActiveEntities + manifest) — sinon « droits requis » en multi-entités.
   */
  public async uploadDocumentToTicket(
    file: Blob,
    filename: string,
    ticketId: number,
    options?: { ticketTitle?: string; entitiesId?: number }
  ): Promise<CreatedRecord> {
    let entitiesId =
      options?.entitiesId != null && options.entitiesId > 0 ? options.entitiesId : undefined;
    if (entitiesId == null) {
      entitiesId = await this.fetchTicketEntitiesId(ticketId);
    }

    const doc = await this.uploadDocument(file, filename, {
      entitiesId,
      displayName: options?.ticketTitle?.trim() || filename,
    });
    const docId = Number(doc.id);
    if (!Number.isFinite(docId) || docId <= 0) {
      throw new GlpiError("GLPI_UPLOAD_ERROR", "Upload document : réponse sans ID valide.", 200);
    }
    return this.linkDocumentToTicket(docId, ticketId, entitiesId);
  }

  /**
   * Upload d'un fichier vers GLPI (étape 1/2).
   *
   * Points critiques GLPI :
   * - uploadManifest = champ texte JSON (pas un Blob/fichier — GLPI lit $_REQUEST)
   * - le fichier va dans filename[0]
   * - URL avec slash final : /Document/
   */
  public async uploadDocument(
    file: Blob,
    filename: string,
    options?: DocumentUploadOptions
  ): Promise<CreatedRecord> {
    const safeName = sanitizeFilename(filename);
    try {
      return await this.doUploadDocument(file, safeName, filename, options);
    } catch (e) {
      if (e instanceof GlpiError && shouldRetryUpload(e)) {
        await this.initSession();
        return this.doUploadDocument(file, safeName, filename, options);
      }
      throw e;
    }
  }

  private async doUploadDocument(
    file: Blob,
    safeName: string,
    originalName: string,
    options?: DocumentUploadOptions
  ): Promise<CreatedRecord> {
    await this.ensureSession();
    if (options?.entitiesId != null && options.entitiesId > 0) {
      try {
        await this.changeActiveEntities(options.entitiesId, true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[GLPI] changeActiveEntities échoué (upload quand même)", (e as Error).message);
      }
    }
    const url = this.buildRestUrl("Document");

    // Stratégies : avec entité (multi-entités), puis sans (manifest minimal).
    const strategies: DocumentFormOptions[] = [];
    if (options?.entitiesId != null && options.entitiesId > 0) {
      strategies.push({
        entitiesId: options.entitiesId,
        displayName: options.displayName || originalName,
      });
    }
    strategies.push({ displayName: options?.displayName || originalName });
    strategies.push({});

    let lastErr: unknown;
    for (let i = 0; i < strategies.length; i++) {
      try {
        const data = await this.tryUploadDocumentOnce(file, safeName, url, strategies[i]);
        return this.finalizeUploadRecord(data, originalName, safeName, file, url);
      } catch (e) {
        lastErr = e;
        const retryable = e instanceof GlpiError && e.httpStatus === 500;
        if (retryable && i < strategies.length - 1) {
          // eslint-disable-next-line no-console
          console.warn("[GLPI] upload HTTP 500, essai manifest alternatif", strategies[i + 1]);
          continue;
        }
        throw e;
      }
    }
    throw lastErr;
  }

  /** Une tentative axios puis fetch — FormData recréé à chaque appel (non réutilisable). */
  private async tryUploadDocumentOnce(
    file: Blob,
    safeName: string,
    url: string,
    formOpts: DocumentFormOptions
  ): Promise<CreatedRecord | CreatedRecord[] | GlpiUploadResponse> {
    try {
      return await this.postDocumentMultipartAxios(
        buildDocumentFormData(file, safeName, formOpts),
        url
      );
    } catch (axiosErr) {
      // eslint-disable-next-line no-console
      console.warn("[GLPI] upload axios échoué, retry fetch", (axiosErr as Error).message);
      return this.postMultipart<CreatedRecord | CreatedRecord[] | GlpiUploadResponse>(
        "Document",
        buildDocumentFormData(file, safeName, formOpts),
        120000
      );
    }
  }

  private finalizeUploadRecord(
    data: CreatedRecord | CreatedRecord[] | GlpiUploadResponse,
    originalName: string,
    safeName: string,
    file: Blob,
    url: string
  ): CreatedRecord {
    const record = normalizeCreatedRecord(data);
    // eslint-disable-next-line no-console
    console.info("[GLPI] uploadDocument", {
      originalName,
      safeName,
      url,
      docId: record.id,
      message: (data as GlpiUploadResponse)?.message,
      blobSize: file.size,
      blobType: file.type,
    });

    if (!Number.isFinite(Number(record.id)) || Number(record.id) <= 0) {
      throw new GlpiError(
        "GLPI_UPLOAD_ERROR",
        `Upload document échoué pour "${safeName}" (pas d'ID retourné).`,
        200
      );
    }
    return record;
  }

  /**
   * Upload Document via axios (même baseURL / Session-Token que le reste de l'API).
   */
  private async postDocumentMultipartAxios(
    formData: FormData,
    url: string
  ): Promise<CreatedRecord | CreatedRecord[] | GlpiUploadResponse> {
    // eslint-disable-next-line no-console
    console.info("[GLPI] postDocumentMultipartAxios", { url });

    const res = await this.http.post<string>("/Document/", formData, {
      timeout: 120000,
      responseType: "text",
      transformResponse: [(body) => body],
      validateStatus: () => true,
      headers: { Accept: "application/json" },
      params: { session_write: true },
    });

    const text = String(res.data ?? "");
    const parsed = this.parseRestResponseBody(text, res.status, url);
    if (res.status < 200 || res.status >= 300) {
      // eslint-disable-next-line no-console
      console.error("[GLPI] upload axios response", {
        status: res.status,
        body: text.length > 800 ? `${text.slice(0, 800)}…` : text,
        parsed,
      });
      throw this.errorFromResponse(parsed, res.status);
    }
    return parsed as CreatedRecord | CreatedRecord[] | GlpiUploadResponse;
  }

  private parseRestResponseBody(text: string, httpStatus: number, url: string): unknown {
    if (looksLikeHtml(text)) {
      throw this.errorFromHtml(text, httpStatus, url);
    }
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      throw new GlpiError(
        "GLPI_PARSE_ERROR",
        `Réponse non JSON (${httpStatus}) sur ${url}`,
        httpStatus
      );
    }
  }

  /**
   * Construit l'URL REST GLPI : https://host/apirest.php/Document/
   * (évite apirest.phpDocument ou /Document hors API).
   */
  private buildRestUrl(itemType: string, query?: Record<string, string>): string {
    let base = this.apiUrl.replace(/\/$/, "");
    if (!/\/apirest\.php$/i.test(base)) {
      base = `${base}/apirest.php`;
    }
    const item = itemType.replace(/^\/+|\/+$/g, "");
    const path = `${base}/${item}/`;
    if (!query || Object.keys(query).length === 0) return path;
    const qs = new URLSearchParams(query).toString();
    return `${path}?${qs}`;
  }

  /**
   * POST multipart via fetch (FormData natif, requis pour l'upload GLPI).
   */
  private async postMultipart<T>(itemType: string, formData: FormData, timeoutMs = 120000): Promise<T> {
    const url = this.buildRestUrl(itemType, { session_write: "true" });
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      const headers: Record<string, string> = {
        "App-Token": this.appToken,
        Accept: "application/json",
      };
      if (this.sessionToken) {
        headers["Session-Token"] = this.sessionToken;
      }

      // eslint-disable-next-line no-console
      console.info("[GLPI] postMultipart", { url, itemType });

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: formData,
        signal: controller.signal,
      });

      const text = await res.text();
      const parsed = this.parseRestResponseBody(text, res.status, url);

      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.error("[GLPI] upload fetch response", {
          status: res.status,
          body: text.length > 800 ? `${text.slice(0, 800)}…` : text,
          parsed,
        });
        throw this.errorFromResponse(parsed, res.status);
      }
      return parsed as T;
    } catch (e) {
      if (e instanceof GlpiError) throw e;
      if (e instanceof Error && e.name === "AbortError") {
        throw new GlpiError("TIMEOUT", "Délai dépassé lors de l'upload du fichier.", 408);
      }
      throw new GlpiError("NETWORK_ERROR", (e as Error).message || "Erreur réseau upload");
    } finally {
      window.clearTimeout(timer);
    }
  }

  private errorFromHtml(html: string, httpStatus: number, url: string): GlpiError {
    const title = html.match(/<title[^>]*>([^<]+)<\/title>/i)?.[1]?.trim();
    const bodyMsg = extractHtmlErrorMessage(html);
    const hint =
      httpStatus === 413
        ? "Fichier trop volumineux (limite PHP/nginx)."
        : "Vérifier les droits Document dans GLPI, upload_max_filesize/post_max_size PHP, et les logs GLPI.";
    const parts = [bodyMsg, title].filter(Boolean);
    const summary = parts.length ? parts.join(" — ") : `Erreur serveur GLPI (HTTP ${httpStatus})`;
    const msg = `${summary}. ${hint}`;
    // eslint-disable-next-line no-console
    console.error("[GLPI] réponse HTML", { url, httpStatus, title });
    return new GlpiError("GLPI_HTML_ERROR", msg, httpStatus);
  }

  private errorFromResponse(data: unknown, httpStatus: number): GlpiError {
    const msg = formatGlpiApiErrorMessage(data, httpStatus);
    const code = extractGlpiApiErrorCode(data) ?? (httpStatus === 500 ? "GLPI_SERVER_ERROR" : "GLPI_ERROR");
    return new GlpiError(code, msg, httpStatus);
  }

  /**
   * Lie un document existant à un ticket (étape 2/2).
   * Le champ "add": "Add" est requis par GLPI pour que le lien apparaisse sur le ticket.
   */
  public async linkDocumentToTicket(
    documentId: number,
    ticketId: number,
    entitiesId?: number
  ): Promise<CreatedRecord> {
    if (entitiesId != null && entitiesId > 0) {
      try {
        await this.changeActiveEntities(entitiesId, true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[GLPI] changeActiveEntities avant lien document", (e as Error).message);
      }
    }
    const res = await this.http.post<CreatedRecord | CreatedRecord[]>(
      "/Document_Item/",
      {
        input: {
          documents_id: documentId,
          items_id: ticketId,
          itemtype: "Ticket",
          add: "Add",
        },
      },
      { params: { session_write: true } }
    );
    const data = normalizeCreatedRecord(res.data);
    // eslint-disable-next-line no-console
    console.info("[GLPI] linkDocumentToTicket", { documentId, ticketId, linkId: data.id });
    return data;
  }

  // -------------------------------------------------------------------------
  // Helpers internes
  // -------------------------------------------------------------------------

  private normalizeError(err: AxiosError): GlpiError {
    // GLPI renvoie typiquement un tableau ["ERROR_CODE", "Message lisible"]
    const httpStatus = err.response?.status;
    const data = err.response?.data;

    if (typeof data === "string" && data.trim()) {
      if (looksLikeHtml(data)) {
        return this.errorFromHtml(data, httpStatus ?? 0, this.apiUrl);
      }
      const short = data.length > 300 ? `${data.slice(0, 300)}…` : data.trim();
      return new GlpiError("GLPI_ERROR", short, httpStatus);
    }

    if (Array.isArray(data) && data.length >= 2) {
      return new GlpiError(String(data[0]), String(data[1]), httpStatus);
    }

    if (data && typeof data === "object" && "message" in data) {
      return new GlpiError(
        "GLPI_ERROR",
        String((data as { message?: unknown }).message ?? "Erreur GLPI"),
        httpStatus
      );
    }

    if (!err.response) {
      // Réseau / CORS / mixed content / timeout
      const isTimeout = err.code === "ECONNABORTED";
      const isMixedContent = this.apiUrl.startsWith("http://") && window.location.protocol === "https:";

      let msg: string;
      if (isTimeout) {
        msg = "Délai dépassé (le serveur GLPI n'a pas répondu à temps).";
      } else if (isMixedContent) {
        msg =
          "URL GLPI en HTTP alors que l'add-in tourne en HTTPS : le navigateur bloque l'appel (mixed content). " +
          "Configure GLPI en HTTPS ou utilise un proxy HTTPS local.";
      } else {
        msg =
          (err.message || "Erreur réseau") +
          " - Vérifie l'URL de l'API GLPI, la configuration CORS du serveur, et que le certificat HTTPS est accepté.";
      }
      return new GlpiError(isMixedContent ? "MIXED_CONTENT" : "NETWORK_ERROR", msg, httpStatus);
    }

    return new GlpiError("UNKNOWN_ERROR", `Erreur HTTP ${httpStatus ?? "?"}`, httpStatus);
  }
}

// -------------------------------------------------------------------------
// Singleton par task pane
// -------------------------------------------------------------------------

let _instance: GlpiClient | null = null;

export function getGlpiClient(): GlpiClient | null {
  return _instance;
}

export function setGlpiClient(client: GlpiClient | null): void {
  _instance = client;
}

export async function createAndInitClient(config: GlpiClientConfig): Promise<GlpiClient> {
  const c = new GlpiClient(config);
  await c.initSession();
  _instance = c;
  return c;
}

/** Masque la valeur d'un token pour les logs : "abcd1234" -> "abcd…" (4 premiers chars). */
function maskToken(t: string | undefined): string {
  if (!t) return "(vide)";
  if (t.length <= 6) return "***";
  return `${t.slice(0, 4)}… (${t.length} chars)`;
}

interface GlpiUploadResponse extends CreatedRecord {
  message?: string;
  upload_result?: unknown;
}

function normalizeCreatedRecord(
  data: CreatedRecord | CreatedRecord[] | GlpiUploadResponse | undefined
): CreatedRecord {
  if (!data) return { id: NaN };
  const row = Array.isArray(data) ? data[0] : data;
  return { id: Number(row.id), message: row.message };
}

/**
 * Nom de fichier sûr pour l'upload GLPI (_filename + part multipart).
 * Espaces, parenthèses, accents → ASCII safe (ex. "Shipping Note (BL).pdf" → "Shipping_Note_BL.pdf").
 */
function looksLikeHtml(text: string): boolean {
  const t = text.trim().slice(0, 200).toLowerCase();
  return t.startsWith("<!doctype") || t.startsWith("<html") || t.includes("<title>erreur");
}

export interface DocumentUploadOptions {
  entitiesId?: number;
  displayName?: string;
}

interface DocumentFormOptions {
  entitiesId?: number;
  displayName?: string;
}

function buildDocumentFormData(file: Blob, safeName: string, opts: DocumentFormOptions = {}): FormData {
  const title = (opts.displayName || safeName).replace(/[/\\?%*:|"<>]/g, "_").slice(0, 200);
  const input: Record<string, unknown> = {
    name: title,
    _filename: [safeName],
  };
  if (opts.entitiesId != null && opts.entitiesId > 0) {
    input.entities_id = opts.entitiesId;
  }
  const manifest = { input };
  const manifestJson = JSON.stringify(manifest);
  const formData = new FormData();
  // GLPI 11 lit $_REQUEST['uploadManifest'] (chaîne JSON), pas $_FILES.
  // Un Blob/fichier ici provoque input=null → HTTP 500 « Une erreur est survenue ».
  formData.append("uploadManifest", manifestJson);
  formData.append("filename[0]", file, safeName);
  if (file.size === 0) {
    // eslint-disable-next-line no-console
    console.warn("[GLPI] upload : fichier 0 octet", safeName);
  }
  // eslint-disable-next-line no-console
  console.info("[GLPI] upload manifest", { manifest, blobSize: file.size });
  return formData;
}

function formatGlpiApiErrorMessage(data: unknown, httpStatus: number): string {
  if (typeof data === "string" && data.trim()) {
    if (looksLikeHtml(data)) return `HTTP ${httpStatus} — page d'erreur GLPI`;
    const s = data.length > 300 ? `${data.slice(0, 300)}…` : data.trim();
    return `HTTP ${httpStatus} — ${s}`;
  }
  if (Array.isArray(data) && data.length >= 2) {
    return `HTTP ${httpStatus} — ${String(data[1])}`;
  }
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    const parts: string[] = [];
    if (typeof o.detail === "string") parts.push(o.detail);
    if (typeof o.title === "string") parts.push(o.title);
    if (Array.isArray(o.message)) parts.push(String(o.message[0] ?? ""));
    else if (typeof o.message === "string") parts.push(o.message);
    if (typeof o.error === "string") parts.push(o.error);
    const text = parts.filter(Boolean).join(" — ");
    if (text) {
      const hint =
        httpStatus === 500
          ? " (erreur serveur : vérifier droits Document, entité active, taille fichier, logs PHP GLPI)"
          : "";
      return `HTTP ${httpStatus} — ${text}${hint}`;
    }
  }
  return `HTTP ${httpStatus} — Erreur GLPI`;
}

function extractGlpiApiErrorCode(data: unknown): string | undefined {
  if (Array.isArray(data) && data.length >= 1) return String(data[0]);
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    if (typeof o.status === "string") return o.status;
  }
  return undefined;
}

function shouldRetryUpload(e: GlpiError): boolean {
  if (e.code === "GLPI_HTML_ERROR" || e.httpStatus === 401) return true;
  const m = e.message.toLowerCase();
  return m.includes("session") || e.code.toUpperCase().includes("SESSION");
}

function extractHtmlErrorMessage(html: string): string | undefined {
  const patterns = [
    /<div[^>]*class="[^"]*alert[^"]*danger[^"]*"[^>]*>[\s\S]*?<p[^>]*>([^<]{3,400})/i,
    /<div[^>]*class="[^"]*alert[^"]*"[^>]*>[\s\S]*?<p[^>]*>([^<]{3,400})/i,
    /<span[^>]*class="[^"]*b[^"]*"[^>]*>([^<]{3,400})<\/span>/i,
  ];
  for (const re of patterns) {
    const m = html.match(re);
    const text = m?.[1]?.replace(/\s+/g, " ").trim();
    if (text && !text.toLowerCase().startsWith("<!")) return text;
  }
  return undefined;
}

function sanitizeFilename(name: string): string {
  const raw = (name || "piece-jointe").trim();
  const dot = raw.lastIndexOf(".");
  const ext = dot > 0 ? raw.slice(dot + 1).replace(/[^a-zA-Z0-9]/g, "").toLowerCase() : "";
  const base = dot > 0 ? raw.slice(0, dot) : raw;
  let safeBase =
    base
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[/\\?%*:|"<>()[\]]/g, "_")
      .replace(/\./g, "_")
      .replace(/\s+/g, "_")
      .replace(/-+/g, "-")
      .replace(/_+/g, "_")
      .replace(/^[_\-.]+|[_\-.]+$/g, "") || "piece-jointe";
  // Bug GLPI < 10.0.4 : noms < 23 car. → "Unauthorized file type".
  if (safeBase.length < 23) {
    safeBase = `${safeBase}_upload`;
    if (safeBase.length < 23) safeBase = safeBase.padEnd(23, "_");
  }
  const safeExt = ext ? `.${ext.slice(0, 10)}` : "";
  const result = `${safeBase}${safeExt}`;
  return result.length > 180 ? result.slice(0, 180) : result;
}

/**
 * Filtre les catégories ITIL applicables à une entité donnée.
 *
 * Une catégorie est applicable si :
 *  - elle appartient exactement à l'entité (entities_id === entityId), OU
 *  - elle est récursive (is_recursive === 1) ET appartient à l'entité racine (0)
 *    ou à un ancêtre. Faute d'avoir l'arbre des entités côté add-in, on accepte
 *    toutes les catégories `is_recursive === 1` quelle que soit leur entité.
 *    Cela reproduit le comportement GLPI : les catégories récursives sont
 *    affichées dans toutes les sous-entités.
 */
export function filterCategoriesForEntity(
  categories: GlpiCategory[],
  entityId: number
): GlpiCategory[] {
  return categories.filter((c) => {
    const sameEntity = Number(c.entities_id) === entityId;
    const isRecursive = Number(c.is_recursive) === 1;
    return sameEntity || isRecursive;
  });
}
